/**
 * Timer Store
 *
 * Manages active timers for feeding and sleep tracking.
 * Persists to localStorage so timers survive page refresh/browser close.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface ActiveTimer {
  id: string;
  babyId: string;
  type: 'FEED' | 'SLEEP' | 'UPRIGHT' | 'TUMMY' | 'KICK_COUNT' | 'CONTRACTION';
  startTime: number; // Unix timestamp in milliseconds
  side?: 'left' | 'right'; // For breastfeeding
  sleepType?: 'nap' | 'night'; // For sleep
  // Track side switches for breastfeeding
  sideSwitches?: Array<{
    side: 'left' | 'right';
    switchedAt: number;
  }>;
  // Pause tracking
  isPaused?: boolean;
  pausedAt?: number; // timestamp when paused
  totalPausedTime?: number; // cumulative milliseconds
  // Countdown target (for UPRIGHT timers)
  targetDurationMs?: number;
  // Head-up tracking (for TUMMY timers — client-side only)
  headUpActive?: boolean;
  headUpSwitches?: Array<{ isUp: boolean; switchedAt: number }>;
}

interface TimerState {
  activeTimers: ActiveTimer[];
  isSyncing: boolean;

  // Actions
  startFeedTimer: (babyId: string, side?: 'left' | 'right', syncToBackend?: boolean) => Promise<string>;
  startSleepTimer: (babyId: string, sleepType?: 'nap' | 'night', syncToBackend?: boolean) => Promise<string>;
  startUprightTimer: (babyId: string, durationMinutes: number, syncToBackend?: boolean) => Promise<string>;
  startTummyTimer: (babyId: string) => Promise<string>;
  startKickCountTimer: (babyId: string) => Promise<string>;
  startContractionTimer: (babyId: string) => Promise<string>;
  toggleHeadUp: (timerId: string) => void;
  getHeadUpSeconds: (timerId: string) => number;
  stopTimer: (timerId: string, syncToBackend?: boolean) => Promise<ActiveTimer | null>;
  switchSide: (timerId: string, syncToBackend?: boolean) => Promise<void>;
  pauseTimer: (timerId: string, syncToBackend?: boolean) => Promise<void>;
  resumeTimer: (timerId: string, syncToBackend?: boolean) => Promise<void>;
  getTotalPausedTime: (timerId: string) => number;
  syncTimersFromBackend: (babyId: string) => Promise<void>;
  handleTimerWSMessage: (msg: any) => void;
  replaceTimer: (localId: string, serverTimer: ActiveTimer) => void;
  getActiveTimer: (babyId: string, type?: 'FEED' | 'SLEEP' | 'UPRIGHT' | 'TUMMY' | 'KICK_COUNT' | 'CONTRACTION') => ActiveTimer | null;
  getTimerById: (timerId: string) => ActiveTimer | null;
  getAllActiveTimers: () => ActiveTimer[];
  clearAllTimers: () => void;
}

export const useTimers = create<TimerState>()(
  persist(
    (set, get) => ({
      activeTimers: [],
      isSyncing: false,

      startFeedTimer: async (babyId: string, side: 'left' | 'right' = 'left', syncToBackend = true) => {
        // Check if there's already an active feed timer for this baby
        const existingTimer = get().activeTimers.find(
          (t) => t.babyId === babyId && t.type === 'FEED'
        );
        if (existingTimer) {
          return existingTimer.id;
        }

        const localId = `local-${crypto.randomUUID()}`;
        const now = Date.now();
        const timer: ActiveTimer = {
          id: localId,
          babyId,
          type: 'FEED',
          startTime: now,
          side,
          sideSwitches: [{ side, switchedAt: now }],
        };

        // Optimistically add to local store
        set((state) => ({
          activeTimers: [...state.activeTimers, timer],
        }));

        // Sync to backend
        if (syncToBackend) {
          try {
            const { timerApi } = await import('../services/api');
            const response = await timerApi.startTimer(babyId, {
              type: 'FEED',
              side: side.toUpperCase() as 'LEFT' | 'RIGHT',
              sideSwitches: timer.sideSwitches?.map(sw => ({
                side: sw.side.toUpperCase() as 'LEFT' | 'RIGHT',
                switchedAt: sw.switchedAt,
              })),
            });

            if (response.success && response.data) {
              const serverData = response.data as any;
              // Replace local timer with server version
              const serverTimer: ActiveTimer = {
                id: serverData.id,
                babyId: serverData.babyId,
                type: serverData.type as ActiveTimer['type'],
                startTime: new Date(serverData.startTime).getTime(),
                side: serverData.side?.toLowerCase() as 'left' | 'right' | undefined,
                sideSwitches: serverData.sideSwitches?.map((sw: any) => ({
                  side: sw.side.toLowerCase() as 'left' | 'right',
                  switchedAt: sw.switchedAt,
                })),
                isPaused: serverData.isPaused || false,
                pausedAt: serverData.pausedAt ? new Date(serverData.pausedAt).getTime() : undefined,
                totalPausedTime: serverData.totalPausedMs || 0,
              };
              get().replaceTimer(localId, serverTimer);
              return serverData.id;
            }
          } catch (error) {
            console.error('Failed to sync timer to backend:', error);
            // Keep local timer on error
          }
        }

        return localId;
      },

      startSleepTimer: async (babyId: string, sleepType: 'nap' | 'night' = 'nap', syncToBackend = true) => {
        // Check if there's already an active sleep timer for this baby
        const existingTimer = get().activeTimers.find(
          (t) => t.babyId === babyId && t.type === 'SLEEP'
        );
        if (existingTimer) {
          return existingTimer.id;
        }

        const localId = `local-${crypto.randomUUID()}`;
        const timer: ActiveTimer = {
          id: localId,
          babyId,
          type: 'SLEEP',
          startTime: Date.now(),
          sleepType,
        };

        // Optimistically add to local store
        set((state) => ({
          activeTimers: [...state.activeTimers, timer],
        }));

        // Sync to backend
        if (syncToBackend) {
          try {
            const { timerApi } = await import('../services/api');
            const response = await timerApi.startTimer(babyId, {
              type: 'SLEEP',
              sleepType: sleepType.toUpperCase() as 'NAP' | 'NIGHT',
            });

            if (response.success && response.data) {
              const serverData = response.data as any;
              const serverTimer: ActiveTimer = {
                id: serverData.id,
                babyId: serverData.babyId,
                type: serverData.type as ActiveTimer['type'],
                startTime: new Date(serverData.startTime).getTime(),
                sleepType: serverData.sleepType?.toLowerCase() as 'nap' | 'night' | undefined,
                isPaused: serverData.isPaused || false,
                pausedAt: serverData.pausedAt ? new Date(serverData.pausedAt).getTime() : undefined,
                totalPausedTime: serverData.totalPausedMs || 0,
              };
              get().replaceTimer(localId, serverTimer);
              return serverData.id;
            }
          } catch (error) {
            console.error('Failed to sync timer to backend:', error);
          }
        }

        return localId;
      },

      startUprightTimer: async (babyId: string, durationMinutes: number, syncToBackend = true) => {
        // Check if there's already an active upright timer for this baby
        const existingTimer = get().activeTimers.find(
          (t) => t.babyId === babyId && t.type === 'UPRIGHT'
        );
        if (existingTimer) {
          return existingTimer.id;
        }

        const targetDurationMs = durationMinutes * 60 * 1000;
        const localId = `local-${crypto.randomUUID()}`;
        const timer: ActiveTimer = {
          id: localId,
          babyId,
          type: 'UPRIGHT',
          startTime: Date.now(),
          targetDurationMs,
        };

        // Optimistically add to local store
        set((state) => ({
          activeTimers: [...state.activeTimers, timer],
        }));

        // Sync to backend
        if (syncToBackend) {
          try {
            const { timerApi } = await import('../services/api');
            const response = await timerApi.startTimer(babyId, {
              type: 'UPRIGHT',
              targetDurationMs,
            });

            if (response.success && response.data) {
              const serverData = response.data as any;
              const serverTimer: ActiveTimer = {
                id: serverData.id,
                babyId: serverData.babyId,
                type: serverData.type as ActiveTimer['type'],
                startTime: new Date(serverData.startTime).getTime(),
                targetDurationMs: serverData.targetDurationMs,
                isPaused: serverData.isPaused || false,
                pausedAt: serverData.pausedAt ? new Date(serverData.pausedAt).getTime() : undefined,
                totalPausedTime: serverData.totalPausedMs || 0,
              };
              get().replaceTimer(localId, serverTimer);
              return serverData.id;
            }
          } catch (error) {
            console.error('Failed to sync upright timer to backend:', error);
          }
        }

        return localId;
      },

      startTummyTimer: async (babyId: string) => {
        const existingTimer = get().activeTimers.find(
          (t) => t.babyId === babyId && t.type === 'TUMMY'
        );
        if (existingTimer) return existingTimer.id;

        const localId = `local-${crypto.randomUUID()}`;
        const now = Date.now();
        const timer: ActiveTimer = {
          id: localId,
          babyId,
          type: 'TUMMY',
          startTime: now,
          headUpActive: false,
          headUpSwitches: [],
        };

        set((state) => ({ activeTimers: [...state.activeTimers, timer] }));

        // Sync to backend
        try {
          const { timerApi } = await import('../services/api');
          const response = await timerApi.startTimer(babyId, { type: 'TUMMY' });
          if (response.success && response.data) {
            const serverData = response.data as any;
            const serverTimer: ActiveTimer = {
              id: serverData.id,
              babyId: serverData.babyId,
              type: 'TUMMY',
              startTime: new Date(serverData.startTime).getTime(),
              isPaused: serverData.isPaused || false,
              pausedAt: serverData.pausedAt ? new Date(serverData.pausedAt).getTime() : undefined,
              totalPausedTime: serverData.totalPausedMs || 0,
              headUpActive: false,
              headUpSwitches: [],
            };
            get().replaceTimer(localId, serverTimer);
            return serverData.id;
          }
        } catch (error) {
          console.error('Failed to sync tummy timer to backend:', error);
        }

        return localId;
      },

      startKickCountTimer: async (babyId: string) => {
        const existingTimer = get().activeTimers.find(
          (t) => t.babyId === babyId && t.type === 'KICK_COUNT'
        );
        if (existingTimer) return existingTimer.id;

        const localId = `local-${crypto.randomUUID()}`;
        const timer: ActiveTimer = {
          id: localId,
          babyId,
          type: 'KICK_COUNT',
          startTime: Date.now(),
        };

        set((state) => ({ activeTimers: [...state.activeTimers, timer] }));

        try {
          const { timerApi } = await import('../services/api');
          const response = await timerApi.startTimer(babyId, { type: 'KICK_COUNT' });
          if (response.success && response.data) {
            const serverData = response.data as any;
            const serverTimer: ActiveTimer = {
              id: serverData.id,
              babyId: serverData.babyId,
              type: 'KICK_COUNT',
              startTime: new Date(serverData.startTime).getTime(),
              isPaused: serverData.isPaused || false,
              pausedAt: serverData.pausedAt ? new Date(serverData.pausedAt).getTime() : undefined,
              totalPausedTime: serverData.totalPausedMs || 0,
            };
            get().replaceTimer(localId, serverTimer);
            return serverData.id;
          }
        } catch (error) {
          console.error('Failed to sync kick count timer to backend:', error);
        }

        return localId;
      },

      startContractionTimer: async (babyId: string) => {
        const existingTimer = get().activeTimers.find(
          (t) => t.babyId === babyId && t.type === 'CONTRACTION'
        );
        if (existingTimer) return existingTimer.id;

        const localId = `local-${crypto.randomUUID()}`;
        const timer: ActiveTimer = {
          id: localId,
          babyId,
          type: 'CONTRACTION',
          startTime: Date.now(),
        };

        set((state) => ({ activeTimers: [...state.activeTimers, timer] }));

        try {
          const { timerApi } = await import('../services/api');
          const response = await timerApi.startTimer(babyId, { type: 'CONTRACTION' });
          if (response.success && response.data) {
            const serverData = response.data as any;
            const serverTimer: ActiveTimer = {
              id: serverData.id,
              babyId: serverData.babyId,
              type: 'CONTRACTION',
              startTime: new Date(serverData.startTime).getTime(),
              isPaused: serverData.isPaused || false,
              pausedAt: serverData.pausedAt ? new Date(serverData.pausedAt).getTime() : undefined,
              totalPausedTime: serverData.totalPausedMs || 0,
            };
            get().replaceTimer(localId, serverTimer);
            return serverData.id;
          }
        } catch (error) {
          console.error('Failed to sync contraction timer to backend:', error);
        }

        return localId;
      },

      toggleHeadUp: (timerId: string) => {
        set((state) => ({
          activeTimers: state.activeTimers.map((t) => {
            if (t.id !== timerId || t.type !== 'TUMMY') return t;
            // Use pause-aware "now" so toggling while paused uses correct timestamp
            const now = t.isPaused && t.pausedAt ? t.pausedAt : Date.now();
            const isNowUp = !t.headUpActive;
            return {
              ...t,
              headUpActive: isNowUp,
              headUpSwitches: [...(t.headUpSwitches || []), { isUp: isNowUp, switchedAt: now }],
            };
          }),
        }));
      },

      getHeadUpSeconds: (timerId: string) => {
        const timer = get().activeTimers.find((t) => t.id === timerId);
        if (!timer || timer.type !== 'TUMMY') return 0;

        const switches = timer.headUpSwitches || [];
        let totalMs = 0;
        let headUpStart: number | null = null;

        for (const sw of switches) {
          if (sw.isUp) {
            headUpStart = sw.switchedAt;
          } else if (headUpStart !== null) {
            totalMs += sw.switchedAt - headUpStart;
            headUpStart = null;
          }
        }

        // If currently head-up, add current session (pause-aware)
        if (timer.headUpActive && headUpStart !== null) {
          const now = timer.isPaused && timer.pausedAt ? timer.pausedAt : Date.now();
          totalMs += now - headUpStart;
        }

        return Math.floor(totalMs / 1000);
      },

      stopTimer: async (timerId: string, syncToBackend = true) => {
        const timer = get().activeTimers.find((t) => t.id === timerId);
        if (!timer) return null;

        // Optimistically remove from local store
        set((state) => ({
          activeTimers: state.activeTimers.filter((t) => t.id !== timerId),
        }));

        // Sync to backend (only if not a local-only timer)
        if (syncToBackend && !timerId.startsWith('local-')) {
          try {
            const { timerApi } = await import('../services/api');
            await timerApi.stopTimer(timerId);
          } catch (error) {
            console.error('Failed to stop timer on backend:', error);
            // Re-add timer on error
            set((state) => ({
              activeTimers: [...state.activeTimers, timer],
            }));
          }
        }

        return timer;
      },

      switchSide: async (timerId: string, syncToBackend = true) => {
        const timer = get().activeTimers.find((t) => t.id === timerId);
        if (!timer || timer.type !== 'FEED') return;

        const newSide = timer.side === 'left' ? 'right' : 'left';
        const now = Date.now();

        // Optimistically update local store
        set((state) => ({
          activeTimers: state.activeTimers.map((t) => {
            if (t.id !== timerId) return t;
            return {
              ...t,
              side: newSide,
              sideSwitches: [
                ...(t.sideSwitches || []),
                { side: newSide, switchedAt: now },
              ],
            };
          }),
        }));

        // Sync to backend
        if (syncToBackend && !timerId.startsWith('local-')) {
          try {
            const { timerApi } = await import('../services/api');
            await timerApi.switchSide(timerId, newSide.toUpperCase() as 'LEFT' | 'RIGHT');
          } catch (error) {
            console.error('Failed to switch side on backend:', error);
          }
        }
      },

      pauseTimer: async (timerId: string, syncToBackend = true) => {
        // Optimistically update local store
        const now = Date.now();
        set((state) => ({
          activeTimers: state.activeTimers.map((t) => {
            if (t.id !== timerId || t.isPaused) return t;
            // For TUMMY timers: if head-up is active, close the current head-up session
            // by adding a synthetic "head down" switch at the pause time.
            // This ensures getHeadUpSeconds correctly freezes head-up time during pause.
            const headUpSwitches = t.type === 'TUMMY' && t.headUpActive
              ? [...(t.headUpSwitches || []), { isUp: false, switchedAt: now }]
              : t.headUpSwitches;
            return {
              ...t,
              isPaused: true,
              pausedAt: now,
              headUpSwitches,
            };
          }),
        }));

        // Sync to backend
        if (syncToBackend && !timerId.startsWith('local-')) {
          try {
            const { timerApi } = await import('../services/api');
            await timerApi.pauseTimer(timerId);
          } catch (error) {
            console.error('Failed to pause timer on backend:', error);
          }
        }
      },

      resumeTimer: async (timerId: string, syncToBackend = true) => {
        const timer = get().activeTimers.find((t) => t.id === timerId);
        if (!timer || !timer.isPaused || !timer.pausedAt) return;

        const now = Date.now();
        const pauseDuration = now - timer.pausedAt;

        // Optimistically update local store
        set((state) => ({
          activeTimers: state.activeTimers.map((t) => {
            if (t.id !== timerId) return t;
            // For TUMMY timers: if head-up was active when paused, restart the
            // head-up session by adding a synthetic "head up" switch at resume time.
            const headUpSwitches = t.type === 'TUMMY' && t.headUpActive
              ? [...(t.headUpSwitches || []), { isUp: true, switchedAt: now }]
              : t.headUpSwitches;
            return {
              ...t,
              isPaused: false,
              pausedAt: undefined,
              totalPausedTime: (t.totalPausedTime || 0) + pauseDuration,
              headUpSwitches,
            };
          }),
        }));

        // Sync to backend
        if (syncToBackend && !timerId.startsWith('local-')) {
          try {
            const { timerApi } = await import('../services/api');
            const response = await timerApi.resumeTimer(timerId);
            // Update with server's totalPausedMs to stay in sync
            if (response.success && response.data) {
              const serverData = response.data as any;
              set((state) => ({
                activeTimers: state.activeTimers.map((t) => {
                  if (t.id !== timerId) return t;
                  return {
                    ...t,
                    totalPausedTime: serverData.totalPausedMs,
                  };
                }),
              }));
            }
          } catch (error) {
            console.error('Failed to resume timer on backend:', error);
          }
        }
      },

      getTotalPausedTime: (timerId: string) => {
        const timer = get().activeTimers.find((t) => t.id === timerId);
        if (!timer) return 0;

        let total = timer.totalPausedTime || 0;
        if (timer.isPaused && timer.pausedAt) {
          total += Date.now() - timer.pausedAt;
        }
        return total;
      },

      getActiveTimer: (babyId: string, type?: 'FEED' | 'SLEEP' | 'UPRIGHT' | 'TUMMY' | 'KICK_COUNT' | 'CONTRACTION') => {
        return (
          get().activeTimers.find(
            (t) => t.babyId === babyId && (type === undefined || t.type === type)
          ) || null
        );
      },

      getTimerById: (timerId: string) => {
        return get().activeTimers.find((t) => t.id === timerId) || null;
      },

      getAllActiveTimers: () => {
        return get().activeTimers;
      },

      syncTimersFromBackend: async (babyId: string) => {
        set({ isSyncing: true });
        try {
          const { timerApi } = await import('../services/api');
          const response = await timerApi.listTimers(babyId);

          if (response.success && response.data) {
            // Snapshot existing local TUMMY timer to preserve client-side head-up state.
            // Server timers don't include headUpActive/headUpSwitches (client-side only).
            const existingLocalTummy = get().activeTimers.find(
              (t) => t.babyId === babyId && t.type === 'TUMMY'
            );

            const serverTimers: ActiveTimer[] = (response.data as any[]).map((t: any) => ({
              id: t.id,
              babyId: t.babyId,
              type: t.type as 'FEED' | 'SLEEP' | 'UPRIGHT' | 'TUMMY',
              startTime: new Date(t.startTime).getTime(),
              side: t.side?.toLowerCase() as 'left' | 'right' | undefined,
              sleepType: t.sleepType?.toLowerCase() as 'nap' | 'night' | undefined,
              isPaused: t.isPaused,
              pausedAt: t.pausedAt ? new Date(t.pausedAt).getTime() : undefined,
              totalPausedTime: t.totalPausedMs,
              targetDurationMs: t.targetDurationMs,
              sideSwitches: t.sideSwitches?.map((sw: any) => ({
                side: sw.side.toLowerCase() as 'left' | 'right',
                switchedAt: sw.switchedAt,
              })),
              // Preserve client-side head-up state for TUMMY timers
              headUpActive: t.type === 'TUMMY' ? (existingLocalTummy?.headUpActive ?? false) : undefined,
              headUpSwitches: t.type === 'TUMMY' ? (existingLocalTummy?.headUpSwitches ?? []) : undefined,
            }));

            // Replace all timers for this baby with server timers
            set((state) => ({
              activeTimers: [
                ...state.activeTimers.filter((t) => t.babyId !== babyId),
                ...serverTimers,
              ],
            }));
          }
        } catch (error) {
          console.error('Failed to sync timers from backend:', error);
        } finally {
          set({ isSyncing: false });
        }
      },

      handleTimerWSMessage: (msg: any) => {
        if (msg.type === 'timer_started' && msg.timer) {
          const timer: ActiveTimer = {
            id: msg.timer.id,
            babyId: msg.timer.babyId,
            type: msg.timer.type as 'FEED' | 'SLEEP' | 'UPRIGHT' | 'TUMMY',
            startTime: new Date(msg.timer.startTime).getTime(),
            side: msg.timer.side?.toLowerCase() as 'left' | 'right' | undefined,
            sleepType: msg.timer.sleepType?.toLowerCase() as 'nap' | 'night' | undefined,
            isPaused: msg.timer.isPaused,
            pausedAt: msg.timer.pausedAt ? new Date(msg.timer.pausedAt).getTime() : undefined,
            totalPausedTime: msg.timer.totalPausedMs || 0,
            targetDurationMs: msg.timer.targetDurationMs,
            sideSwitches: msg.timer.sideSwitches?.map((sw: any) => ({
              side: sw.side.toLowerCase() as 'left' | 'right',
              switchedAt: sw.switchedAt,
            })),
          };

          set((state) => {
            // Check if timer already exists
            const exists = state.activeTimers.some((t) => t.id === timer.id);
            if (exists) return state;

            return {
              activeTimers: [...state.activeTimers, timer],
            };
          });
        } else if (msg.type === 'timer_paused') {
          set((state) => ({
            activeTimers: state.activeTimers.map((t) => {
              if (t.id !== msg.timerId) return t;
              return {
                ...t,
                isPaused: true,
                pausedAt: msg.pausedAt ? new Date(msg.pausedAt).getTime() : Date.now(),
              };
            }),
          }));
        } else if (msg.type === 'timer_resumed') {
          set((state) => ({
            activeTimers: state.activeTimers.map((t) => {
              if (t.id !== msg.timerId) return t;
              return {
                ...t,
                isPaused: false,
                pausedAt: undefined,
                totalPausedTime: msg.totalPausedMs || t.totalPausedTime || 0,
              };
            }),
          }));
        } else if (msg.type === 'timer_side_switched') {
          set((state) => ({
            activeTimers: state.activeTimers.map((t) => {
              if (t.id !== msg.timerId) return t;
              return {
                ...t,
                side: msg.newSide?.toLowerCase() as 'left' | 'right',
                sideSwitches: msg.sideSwitches?.map((sw: any) => ({
                  side: sw.side.toLowerCase() as 'left' | 'right',
                  switchedAt: sw.switchedAt,
                })),
              };
            }),
          }));
        } else if (msg.type === 'timer_stopped') {
          set((state) => ({
            activeTimers: state.activeTimers.filter((t) => t.id !== msg.timerId),
          }));
        }
      },

      replaceTimer: (localId: string, serverTimer: ActiveTimer) => {
        set((state) => ({
          activeTimers: state.activeTimers.map((t) => (t.id === localId ? serverTimer : t)),
        }));
      },

      clearAllTimers: () => {
        set({ activeTimers: [] });
      },
    }),
    {
      name: 'mytottot-timers',
    }
  )
);

/**
 * Calculate elapsed time in seconds from a start timestamp, accounting for paused time
 */
export function calculateElapsedSeconds(
  startTime: number,
  totalPausedTime = 0,
  isPaused = false,
  pausedAt?: number
): number {
  const now = isPaused && pausedAt ? pausedAt : Date.now();
  const gross = now - startTime;
  return Math.max(0, Math.floor((gross - totalPausedTime) / 1000));
}

/**
 * Calculate elapsed time in minutes from a start timestamp, accounting for paused time
 */
export function calculateElapsedMinutes(
  startTime: number,
  totalPausedTime = 0,
  isPaused = false,
  pausedAt?: number
): number {
  const seconds = calculateElapsedSeconds(startTime, totalPausedTime, isPaused, pausedAt);
  return Math.floor(seconds / 60);
}

/**
 * Format elapsed seconds as HH:MM:SS or MM:SS
 */
export function formatElapsedTime(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Get duration per side for breastfeeding
 */
export function getSideDurations(timer: ActiveTimer): { left: number; right: number } {
  if (!timer.sideSwitches || timer.sideSwitches.length === 0) {
    return { left: 0, right: 0 };
  }

  const now = Date.now();
  let leftMs = 0;
  let rightMs = 0;

  for (let i = 0; i < timer.sideSwitches.length; i++) {
    const current = timer.sideSwitches[i];
    const next = timer.sideSwitches[i + 1];
    const endTime = next ? next.switchedAt : now;
    const duration = endTime - current.switchedAt;

    if (current.side === 'left') {
      leftMs += duration;
    } else {
      rightMs += duration;
    }
  }

  return {
    left: Math.floor(leftMs / 60000), // Convert to minutes
    right: Math.floor(rightMs / 60000),
  };
}
